/**
 * MealScanner - Mobile-friendly meal scanning component with QR/Barcode support
 * Shows green (can eat) or red (already eaten) status indicators
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';
import { mealAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  QrCode, Scan, Check, X, User, Utensils, Clock, AlertTriangle,
  Loader2, Camera, RefreshCw, ChevronDown, Search, ScanLine
} from 'lucide-react';

const MealScanner = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [selectedMeal, setSelectedMeal] = useState('');
  const [scanInput, setScanInput] = useState('');
  const [lastScanResult, setLastScanResult] = useState(null);
  const [showCameraDialog, setShowCameraDialog] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeCamera, setActiveCamera] = useState(null);
  const inputRef = useRef(null);
  const html5QrRef = useRef(null);

  // Fetch meals for this event
  const { data: meals = [] } = useQuery({
    queryKey: ['meals', eventId],
    queryFn: () => mealAPI.adminList(eventId).then(r => r.data),
    enabled: !!eventId,
  });

  // Fetch scan summary
  const { data: scanSummary } = useQuery({
    queryKey: ['meal-scans-summary', eventId],
    queryFn: () => mealAPI.getScansSummary(eventId).then(r => r.data),
    enabled: !!eventId,
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Scan mutation
  const scanMutation = useMutation({
    mutationFn: ({ mealId, delegateId }) => mealAPI.scan(eventId, mealId, {
      delegate_id: delegateId,
      meal_id: mealId,
      scan_method: showCameraDialog ? 'qr' : 'manual'
    }),
    onSuccess: (response) => {
      const data = response.data;
      setLastScanResult(data);
      
      if (data.can_eat) {
        toast.success(`✓ Approved: ${data.delegate_name}`, {
          description: data.dietary_preference ? `Dietary: ${data.dietary_preference}` : undefined,
        });
      } else {
        toast.error(`✗ Denied: ${data.delegate_name}`, {
          description: data.message,
        });
      }
      
      queryClient.invalidateQueries(['meal-scans-summary', eventId]);
      setScanInput('');
      
      // Auto-focus input for next scan
      setTimeout(() => inputRef.current?.focus(), 100);
    },
    onError: (err) => {
      const message = err.response?.data?.detail || 'Scan failed';
      setLastScanResult({
        status: 'error',
        can_eat: false,
        message,
        color: 'red'
      });
      toast.error(message);
      setScanInput('');
    },
  });

  // Initialize camera
  useEffect(() => {
    Html5Qrcode.getCameras()
      .then(devices => {
        const backCamera = devices.find(d => 
          d.label.toLowerCase().includes('back') || 
          d.label.toLowerCase().includes('rear')
        );
        setActiveCamera(backCamera?.id || devices[0]?.id);
      })
      .catch(err => console.error('Camera error:', err));
    
    return () => stopCameraScanning();
  }, []);

  // Camera scanning functions
  const startCameraScanning = async () => {
    if (!activeCamera) {
      toast.error('No camera available');
      return;
    }
    if (!selectedMeal) {
      toast.error('Please select a meal first');
      return;
    }

    setShowCameraDialog(true);
    setLastScanResult(null);

    setTimeout(async () => {
      try {
        html5QrRef.current = new Html5Qrcode('meal-scanner-camera');
        await html5QrRef.current.start(
          activeCamera,
          { fps: 15, qrbox: { width: 250, height: 250 } },
          onCameraScanSuccess,
          () => {}
        );
        setIsScanning(true);
      } catch (err) {
        console.error('Camera start error:', err);
        toast.error('Failed to start camera');
        setShowCameraDialog(false);
      }
    }, 300);
  };

  const stopCameraScanning = async () => {
    if (html5QrRef.current) {
      try {
        if (html5QrRef.current.isScanning) {
          await html5QrRef.current.stop();
        }
        html5QrRef.current.clear();
        html5QrRef.current = null;
      } catch (e) {}
    }
    setIsScanning(false);
  };

  const closeCameraDialog = async () => {
    await stopCameraScanning();
    setShowCameraDialog(false);
  };

  const onCameraScanSuccess = useCallback(async (decodedText) => {
    if (isProcessing) return;
    setIsProcessing(true);
    
    // Pause scanner
    if (html5QrRef.current) {
      try { await html5QrRef.current.pause(); } catch (e) {}
    }

    // Extract delegate ID
    let delegateId = decodedText.trim();
    try {
      const qrData = JSON.parse(decodedText);
      if (qrData.delegate_id) delegateId = qrData.delegate_id;
      else if (qrData.id) delegateId = qrData.id;
    } catch {}

    // Process the scan
    try {
      const response = await mealAPI.scan(eventId, selectedMeal, {
        delegate_id: delegateId,
        meal_id: selectedMeal,
        scan_method: 'qr'
      });
      
      const data = response.data;
      setLastScanResult(data);
      
      if (data.can_eat) {
        toast.success(`✓ Approved: ${data.delegate_name}`);
        // Close after success
        setTimeout(() => closeCameraDialog(), 1500);
      } else {
        toast.error(`✗ Denied: ${data.delegate_name}`, { description: data.message });
        // Resume for next scan
        if (html5QrRef.current) {
          try { await html5QrRef.current.resume(); } catch (e) {}
        }
      }
      
      queryClient.invalidateQueries(['meal-scans-summary', eventId]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
      // Resume for retry
      if (html5QrRef.current) {
        try { await html5QrRef.current.resume(); } catch (e) {}
      }
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, eventId, selectedMeal, queryClient]);

  // Handle scan input (QR code data or delegate ID)
  const handleScan = () => {
    if (!selectedMeal) {
      toast.error('Please select a meal first');
      return;
    }
    if (!scanInput.trim()) {
      toast.error('Please enter or scan a delegate ID');
      return;
    }

    // Extract delegate ID from QR data or use directly
    let delegateId = scanInput.trim();
    
    // If QR contains JSON data, extract delegate_id
    try {
      const qrData = JSON.parse(scanInput);
      if (qrData.delegate_id) {
        delegateId = qrData.delegate_id;
      } else if (qrData.id) {
        delegateId = qrData.id;
      }
    } catch {
      // Not JSON, use as-is (direct delegate ID)
    }

    scanMutation.mutate({ mealId: selectedMeal, delegateId });
  };

  // Handle Enter key for quick scanning
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleScan();
    }
  };

  // Auto-select first meal
  useEffect(() => {
    if (meals.length > 0 && !selectedMeal) {
      setSelectedMeal(meals[0].id);
    }
  }, [meals, selectedMeal]);

  // Auto-focus input
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const selectedMealData = meals.find(m => m.id === selectedMeal);
  const selectedMealSummary = scanSummary?.meals?.find(m => m.meal_id === selectedMeal);

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-6">
      <div className="max-w-lg mx-auto space-y-4">
        {/* Header */}
        <div className="text-center pb-2">
          <h1 className="text-2xl font-bold flex items-center justify-center gap-2">
            <Utensils className="h-6 w-6" />
            Meal Scanner
          </h1>
          <p className="text-slate-500 text-sm">Scan delegates for meal access</p>
        </div>

        {/* Meal Selection */}
        <Card>
          <CardContent className="pt-4">
            <Select value={selectedMeal} onValueChange={setSelectedMeal}>
              <SelectTrigger className="w-full h-14 text-lg" data-testid="meal-select">
                <SelectValue placeholder="Select meal..." />
              </SelectTrigger>
              <SelectContent>
                {meals.map((meal) => (
                  <SelectItem key={meal.id} value={meal.id} className="py-3">
                    <div className="flex items-center gap-2">
                      <Utensils className="h-4 w-4" />
                      <span className="font-medium">{meal.name}</span>
                      <Badge variant="outline" className="text-xs">{meal.type}</Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Meal Stats */}
            {selectedMealSummary && (
              <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
                <span>{selectedMealSummary.scanned} served</span>
                <span>{selectedMealSummary.remaining} remaining</span>
                <Badge variant="secondary">{selectedMealSummary.percentage}</Badge>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scan Input */}
        <Card>
          <CardContent className="pt-4 space-y-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  ref={inputRef}
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Scan QR or enter delegate ID..."
                  className="pl-10 h-14 text-lg"
                  data-testid="scan-input"
                  autoFocus
                />
              </div>
              <Button
                onClick={handleScan}
                disabled={!selectedMeal || !scanInput || scanMutation.isPending}
                className="h-14 px-6"
                data-testid="scan-btn"
              >
                {scanMutation.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Scan className="h-5 w-5" />
                )}
              </Button>
            </div>

            {/* Camera Scan Button */}
            <Button
              onClick={startCameraScanning}
              disabled={!selectedMeal}
              variant="outline"
              className="w-full h-12 gap-2"
              data-testid="camera-scan-btn"
            >
              <Camera className="h-5 w-5" />
              Open Camera Scanner
            </Button>

            <p className="text-xs text-center text-slate-400">
              Use camera or type delegate ID and press Enter
            </p>
          </CardContent>
        </Card>

        {/* Camera Scanner Dialog */}
        <Dialog open={showCameraDialog} onOpenChange={closeCameraDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Camera className="h-5 w-5" />
                Meal Scanner - {selectedMealData?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div 
                id="meal-scanner-camera"
                className="w-full aspect-square bg-black rounded-lg overflow-hidden"
              />
              
              {isScanning && !isProcessing && (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-full">
                    <ScanLine className="h-4 w-4 animate-pulse" />
                    <span>Point camera at delegate badge</span>
                  </div>
                </div>
              )}
              
              {isProcessing && (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Verifying...</span>
                  </div>
                </div>
              )}

              {lastScanResult && (
                <div className={`p-4 rounded-lg text-center ${lastScanResult.can_eat ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div className={`w-16 h-16 mx-auto mb-2 rounded-full flex items-center justify-center ${
                    lastScanResult.can_eat ? 'bg-green-500' : 'bg-red-500'
                  }`}>
                    {lastScanResult.can_eat ? (
                      <Check className="h-10 w-10 text-white" />
                    ) : (
                      <X className="h-10 w-10 text-white" />
                    )}
                  </div>
                  <p className={`font-bold text-xl ${lastScanResult.can_eat ? 'text-green-700' : 'text-red-700'}`}>
                    {lastScanResult.can_eat ? 'APPROVED' : 'DENIED'}
                  </p>
                  {lastScanResult.delegate_name && (
                    <p className="text-slate-600">{lastScanResult.delegate_name}</p>
                  )}
                </div>
              )}

              <Button onClick={closeCameraDialog} variant="outline" className="w-full">
                <X className="h-4 w-4 mr-2" /> Close Scanner
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Last Scan Result - Large Visual Indicator */}
        {lastScanResult && (
          <Card className={`border-4 ${lastScanResult.can_eat ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
            <CardContent className="py-8 text-center">
              {/* Large Status Icon */}
              <div className={`w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center ${
                lastScanResult.can_eat ? 'bg-green-500' : 'bg-red-500'
              }`}>
                {lastScanResult.can_eat ? (
                  <Check className="h-14 w-14 text-white" strokeWidth={3} />
                ) : (
                  <X className="h-14 w-14 text-white" strokeWidth={3} />
                )}
              </div>

              {/* Status Text */}
              <h2 className={`text-3xl font-bold mb-2 ${
                lastScanResult.can_eat ? 'text-green-700' : 'text-red-700'
              }`}>
                {lastScanResult.can_eat ? 'APPROVED' : 'DENIED'}
              </h2>

              {/* Delegate Info */}
              <div className="space-y-1">
                <p className="text-xl font-medium">{lastScanResult.delegate_name || 'Unknown'}</p>
                <p className="text-slate-600">{lastScanResult.message}</p>
                {lastScanResult.dietary_preference && lastScanResult.dietary_preference !== 'None' && (
                  <Badge variant="outline" className="mt-2">
                    Dietary: {lastScanResult.dietary_preference}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Stats */}
        {scanSummary && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Today's Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-center">
                {scanSummary.meals?.slice(0, 3).map((meal) => (
                  <div key={meal.meal_id} className="p-2 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 truncate">{meal.meal_name}</p>
                    <p className="text-lg font-bold">{meal.scanned}</p>
                    <p className="text-xs text-slate-400">{meal.percentage}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Alert>
          <QrCode className="h-4 w-4" />
          <AlertDescription className="text-sm">
            <strong>How to scan:</strong>
            <ol className="list-decimal ml-4 mt-1 space-y-1">
              <li>Select the meal above</li>
              <li>Scan delegate's QR code or enter their ID</li>
              <li>Green = Serve food, Red = Already served</li>
            </ol>
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
};

export default MealScanner;
